import { visit } from '@takeshape/util';
import { isObjectSchemaWithProperties, isSchema } from "./type-utils.js";
function collectRefs(def, key, collect) {
    if (!isSchema(def)) {
        return;
    }
    visit(def, ['$ref'], ($ref) => {
        if ($ref && typeof $ref === 'string') {
            collect($ref, key);
        }
    });
}
export function getDefinitionKey(ref) {
    const [, prefix, name] = ref.split('/');
    if (!name || (prefix !== 'definitions' && prefix !== '$defs')) {
        throw new Error(`Invalid reference: ${ref}`);
    }
    return name;
}
/**
 * Get all the definitions that are referenced by other definitions.
 */
export function getReferenceMap(definitions) {
    const references = new Map();
    const visited = new Set();
    const addToReferenceMap = (ref, key) => {
        const refKey = getDefinitionKey(ref);
        // Circular reference
        if (key === refKey) {
            return;
        }
        const visitKey = `${key}->${refKey}`;
        if (visited.has(visitKey))
            return;
        visited.add(visitKey);
        const refSet = references.get(key) ?? new Set();
        refSet.add(refKey);
        references.set(key, refSet);
        const refDef = definitions[refKey];
        if (refDef && isSchema(refDef)) {
            collectRefs(refDef, key, addToReferenceMap);
        }
    };
    for (const [key, def] of Object.entries(definitions)) {
        if (isSchema(def)) {
            collectRefs(def, key, addToReferenceMap);
        }
    }
    return references;
}
/**
 * Prunes the schema definitions to only include those that are referenced either directly
 * or indirectly by the specified root reference.
 *
 * @param {JSONSchema7} schema - The JSON schema object containing the definitions to prune.
 * @param {string} ref - The reference identifier that serves as the root for pruning definitions.
 * @return {JSONSchema7} - A new JSON schema object with pruned definitions.
 */
export function pruneSchemaDefinitions(schema, ref) {
    if (!schema.definitions) {
        throw new Error('Schema does not have definitions');
    }
    const references = new Set();
    const visited = new Set();
    const addToReferences = (ref) => {
        const definitionKey = getDefinitionKey(ref);
        if (visited.has(definitionKey)) {
            return;
        }
        visited.add(definitionKey);
        references.add(definitionKey);
        const refDef = schema.definitions?.[definitionKey];
        if (refDef && isSchema(refDef)) {
            collectRefs(refDef, definitionKey, addToReferences);
        }
    };
    addToReferences(ref);
    const newDefinitions = {};
    for (const ref of references) {
        const def = schema.definitions?.[ref];
        if (def) {
            newDefinitions[ref] = def;
        }
    }
    return {
        ...schema,
        definitions: newDefinitions
    };
}
/**
 * Promotes a specific object schema definition within a JSON Schema, potentially simplifying the schema
 * by focusing on the desired object and pruning unused definitions.
 *
 * Note this does not support promoting schemas that are self-referential
 *
 * @param {JSONSchema7} schema - The full JSON Schema to process.
 * @param {string} ref - The reference string identifying the target object schema within the definitions.
 * @return {ObjectSchemaWithProperties} The promoted object schema along with pruned definitions.
 * @throws {Error} If the specified reference does not correspond to a valid object definition within the schema.
 */
export function promoteObjectSchema(schema, ref) {
    const key = getDefinitionKey(ref);
    const definition = schema.definitions?.[key];
    if (!definition || !isObjectSchemaWithProperties(definition)) {
        throw new Error(`Could not find object definition for ${ref}`);
    }
    const prunedSchema = pruneSchemaDefinitions(schema, ref);
    const { [key]: _, ...rest } = prunedSchema.definitions;
    return {
        ...definition,
        definitions: rest
    };
}
